import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useLoginMutation } from "../../../store/api/authApi";

interface LoginFormProps {
	// No longer needed - kept for backward compatibility but not used
	selectedRole?: null | undefined;
}

interface LoginFormState {
	email: string;
	password: string;
}

const LoginForm: React.FC<LoginFormProps> = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [login, { isLoading }] = useLoginMutation();
	const [formData, setFormData] = useState<LoginFormState>({
		email: "",
		password: "",
	});
	const [error, setError] = useState<string>("");
	const [showPassword, setShowPassword] = useState(false);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		// Normalize email to lowercase
		const normalizedValue = name === "email" ? value.toLowerCase() : value;
		setFormData((prev) => ({
			...prev,
			[name]: normalizedValue,
		}));
		if (error) setError("");
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError("");

		if (!formData.email || !formData.password) {
			setError("Please fill in all fields");
			return;
		}

		try {
			// Normalize email to lowercase before sending
			const normalizedEmail = formData.email.trim().toLowerCase();
			// Call the login API with email and password only
			const result = await login({
				email: normalizedEmail,
				password: formData.password,
			}).unwrap();

			// Tokens are automatically stored by the API interceptor
			// Get the user role from the response
			const userRole = result.user.role;

			// Get the intended destination from URL query params or location state
			const searchParams = new URLSearchParams(location.search);
			const returnUrl = searchParams.get("returnUrl");
			const from = (location.state as { from?: { pathname: string } })?.from
				?.pathname;

			// Priority: returnUrl > from > default role-based redirect
			if (returnUrl && returnUrl !== "/login") {
				navigate(returnUrl, { replace: true });
			} else if (from && from !== "/login") {
				navigate(from, { replace: true });
			} else {
				// Default role-based redirect
				switch (userRole) {
					case "admin":
						navigate("/admin-dashboard", { replace: true });
						break;
					case "tenant":
						navigate("/tenant-dashboard", { replace: true });
						break;
					case "technician":
						navigate("/technician-dashboard", { replace: true });
						break;
					case "agent":
						navigate("/agent-dashboard", { replace: true });
						break;
					default:
						navigate("/", { replace: true });
				}
			}
		} catch (err: any) {
			console.error("Login failed:", err);

			// Handle different error types
			if (err?.data?.error) {
				setError(err.data.error);
			} else if (err?.data?.message) {
				setError(err.data.message);
			} else if (err?.status === 401) {
				setError("Invalid email or password. Please try again.");
			} else if (err?.status === 400) {
				setError("Please check your credentials and try again.");
			} else if (err?.status === "FETCH_ERROR") {
				setError("Network error. Please check your connection and try again.");
			} else {
				setError("Login failed. Please try again.");
			}
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div>
				<label className="mb-2 block text-sm font-medium text-foreground">
					Email Address
				</label>
				<Input
					type="email"
					name="email"
					value={formData.email}
					onChange={handleInputChange}
					placeholder="Enter your email"
					required
				/>
			</div>

			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<label className="block text-sm font-medium text-foreground">
						Password
					</label>
					<button
						type="button"
						onClick={() => navigate("/password-reset")}
						className="text-sm text-primary hover:text-primary/80 transition-colors"
					>
						Forgot Password?
					</button>
				</div>
				<div className="relative">
					<input
						type={showPassword ? "text" : "password"}
						name="password"
						value={formData.password}
						onChange={handleInputChange}
						placeholder="Enter your password"
						required
						className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					/>
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
						aria-label={showPassword ? "Hide password" : "Show password"}
					>
						<Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
					</button>
				</div>
			</div>

			{error && (
				<div className="rounded-lg bg-destructive/10 p-3 text-center text-sm text-destructive">
					{error}
				</div>
			)}

			<Button type="submit" className="w-full" disabled={isLoading}>
				{isLoading ? (
					<>
						<Icon name="Loader2" size={16} className="mr-2 animate-spin" />
						Signing in...
					</>
				) : (
					<>
						<Icon name="LogIn" size={16} className="mr-2" />
						Sign In
					</>
				)}
			</Button>

			<div className="text-center">
				<button
					type="button"
					className="text-sm text-muted-foreground transition-colors hover:text-primary"
					onClick={() => navigate("/")}
				>
					← Back to Home
				</button>
			</div>
		</form>
	);
};

export default LoginForm;
